import { useState, useEffect, useCallback, useRef } from 'react';

// =====================================================================
// useAgendaMs — TODA a conversa com a agenda Teams/M365 (/api/ms/*)
// =====================================================================
// Extraído da AgendaCorporativa quando a agenda ganhou uma SEGUNDA apresentação:
// o card em lista (Home) e o grid de mês (modal do ícone de calendário). A lógica
// é a mesma nos dois — OAuth, status, leitura, criar/editar/excluir —, só o
// desenho muda. Sem este hook, o componente viraria "dois modos numa peça só" ou
// a regra nasceria duplicada nas duas views.
//
// O hook NÃO decide a janela de tempo: o card pede "próximos 7/30 dias" e o grid
// pede "o mês tal". Quem chama passa início/fim em `carregarEventos(ini, fim)`.
// Ao montar, só o STATUS é consultado — a busca dos compromissos fica com a view
// (que sabe qual janela quer) num efeito disparado quando `estado` vira
// 'conectado'.
//
// Devolve:
//   estado            : 'carregando' | 'indisponivel' | 'desconectado' | 'conectado' | 'erro'
//   email             : conta Microsoft vinculada (só quando conectado)
//   eventos           : compromissos da última janela carregada
//   carregandoEventos : true enquanto a janela está sendo buscada
//   conectando        : true entre o clique em "Conectar" e a abertura do popup
//   erroMsg           : erro de operação (não de conexão) — a view exibe
//   carregarStatus / carregarEventos / conectar / desconectar / salvar / excluir

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

export default function useAgendaMs() {
    const [estado, setEstado] = useState('carregando');
    const [email, setEmail] = useState(null);
    // Quem pode CRIAR compromisso (escritório/gerente) — vem do /status, decidido
    // pelo backend. Só esconde o botão; a trava de verdade é a rota (403).
    const [podeCriar, setPodeCriar] = useState(false);
    const [eventos, setEventos] = useState([]);
    const [carregandoEventos, setCarregandoEventos] = useState(false);
    const [conectando, setConectando] = useState(false);
    const [erroMsg, setErroMsg] = useState('');
    const popupRef = useRef(null);
    // Última janela pedida — permite recarregar depois de salvar/excluir sem a
    // view ter de repassar as datas de novo.
    const janelaRef = useRef(null);

    // ---- Status de conexão -------------------------------------------------
    const carregarStatus = useCallback(async () => {
        try {
            const r = await fetch('/api/ms/status', { headers: authHeaders() });
            if (r.status === 503) { setEstado('indisponivel'); return false; }
            if (!r.ok) { setEstado('erro'); return false; }
            const d = await r.json();
            setPodeCriar(!!d.podeCriar);
            if (d.connected) { setEmail(d.email); setEstado('conectado'); return true; }
            setEstado('desconectado');
            return false;
        } catch {
            setEstado('erro');
            return false;
        }
    }, []);

    // ---- Leitura dos compromissos -----------------------------------------
    // `ini`/`fim` são Date em horário LOCAL (Brasília). toISOString() manda em UTC
    // absoluto, então o fuso do servidor (produção roda em UTC) não desloca a janela.
    const carregarEventos = useCallback(async (ini, fim) => {
        const janela = ini && fim ? { ini, fim } : janelaRef.current;
        if (!janela) return;
        janelaRef.current = janela;
        setCarregandoEventos(true);
        setErroMsg('');
        try {
            const qs = `?start=${encodeURIComponent(janela.ini.toISOString())}&end=${encodeURIComponent(janela.fim.toISOString())}`;
            // cache: 'no-store' -> a lista sempre reflete o que acabou de ser criado/editado.
            const r = await fetch(`/api/ms/agenda${qs}`, { headers: authHeaders(), cache: 'no-store' });
            if (r.status === 409) { setEstado('desconectado'); return; }   // vínculo sumiu
            if (!r.ok) { setErroMsg('Não foi possível carregar os compromissos.'); return; }
            const dados = await r.json();
            setEventos(dados.eventos || []);
        } catch {
            setErroMsg('Falha de rede ao buscar a agenda.');
        } finally {
            setCarregandoEventos(false);
        }
    }, []);

    // Ao montar: só o status. A janela é responsabilidade da view.
    useEffect(() => { carregarStatus(); }, [carregarStatus]);

    // ---- Conectar (OAuth via popup) ---------------------------------------
    // Pega a URL de consentimento no backend e abre num popup. Quando o popup
    // termina, o callback do backend faz postMessage('ms-agenda') -> recarrega.
    const conectar = useCallback(async () => {
        setConectando(true);
        setErroMsg('');
        try {
            const r = await fetch('/api/ms/login', { headers: authHeaders() });
            if (r.status === 503) { setEstado('indisponivel'); return; }
            if (!r.ok) { setErroMsg('Não foi possível iniciar a conexão.'); return; }
            const { url } = await r.json();
            const largura = 520, altura = 640;
            const left = window.screenX + (window.outerWidth - largura) / 2;
            const top = window.screenY + (window.outerHeight - altura) / 2;
            popupRef.current = window.open(url, 'ms-agenda-oauth', `width=${largura},height=${altura},left=${left},top=${top}`);
        } catch {
            setErroMsg('Falha ao abrir a janela de conexão.');
        } finally {
            setConectando(false);
        }
    }, []);

    // Escuta o aviso do popup (mesma origem do site). Só mexe no STATUS: quem
    // recarrega a janela é o efeito da view, que reage a `estado` virar 'conectado'.
    useEffect(() => {
        const onMsg = (e) => {
            if (e.origin !== window.location.origin) return;   // segurança: só a própria origem
            if (e.data?.tipo === 'ms-agenda' && e.data.sucesso) {
                if (popupRef.current) { try { popupRef.current.close(); } catch { /* popup já fechado */ } }
                carregarStatus();
            }
        };
        window.addEventListener('message', onMsg);
        return () => window.removeEventListener('message', onMsg);
    }, [carregarStatus]);

    // ---- Desconectar ------------------------------------------------------
    const desconectar = useCallback(async () => {
        if (!window.confirm('Desconectar sua agenda do Teams deste portal?')) return;
        try {
            await fetch('/api/ms/disconnect', { method: 'DELETE', headers: authHeaders() });
        } catch { /* ignora — segue limpando a UI */ }
        setEmail(null); setEventos([]); setEstado('desconectado');
    }, []);

    // ---- Criar / editar ---------------------------------------------------
    // `form` vem da tela: { id?, assunto, inicio, fim, local, participantes, online }.
    // inicio/fim são 'YYYY-MM-DDTHH:mm' do <input type="datetime-local"> — o backend
    // é quem embrulha no fuso. Devolve true se salvou (a view fecha o formulário).
    const salvar = useCallback(async (form) => {
        if (!form.assunto.trim()) { setErroMsg('Informe o título do compromisso.'); return false; }
        // Editar uma SÉRIE recorrente não mexe em data/hora nem no padrão: mudar a
        // data do mestre deslocaria a série inteira. A tela nem mostra os campos de
        // data nesse caso — aqui só não os enviamos (nem valida, nem manda).
        const editandoSerie = !!form.id && !!form.serie;
        if (!editandoSerie) {
            if (!form.inicio || !form.fim) { setErroMsg('Informe início e fim.'); return false; }
            if (form.fim <= form.inicio) { setErroMsg('O fim precisa ser depois do início.'); return false; }
        }
        const corpo = {
            assunto: form.assunto.trim(),
            local: (form.local || '').trim(),
            participantes: (form.participantes || '').trim(),
            online: !!form.online,
        };
        if (!editandoSerie) {
            corpo.inicio = `${form.inicio}:00`;
            corpo.fim = `${form.fim}:00`;
            // Recorrência só na CRIAÇÃO (série nova). Na edição não reenviamos — o
            // PATCH parcial preserva o padrão já gravado no Graph.
            if (!form.id && form.recorrencia) corpo.recorrencia = form.recorrencia;
        }
        const editando = !!form.id;
        try {
            const r = await fetch(
                editando ? `/api/ms/agenda/${encodeURIComponent(form.id)}` : '/api/ms/agenda',
                {
                    method: editando ? 'PATCH' : 'POST',
                    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                    body: JSON.stringify(corpo),
                }
            );
            if (!r.ok) {
                // Mostra a mensagem do backend quando houver: o 403 da regra
                // escritório/gerente explica POR QUE não salvou. Um "não foi
                // possível" genérico deixaria a pessoa tentando de novo à toa.
                const b = await r.json().catch(() => ({}));
                setErroMsg(b.message || 'Não foi possível salvar o compromisso.');
                return false;
            }
            carregarEventos();   // recarrega a MESMA janela
            return true;
        } catch {
            setErroMsg('Falha de rede ao salvar.');
            return false;
        }
    }, [carregarEventos]);

    // ---- Excluir ----------------------------------------------------------
    const excluir = useCallback(async (ev) => {
        // Recorrente: mira o MESTRE (a série inteira). `ev` chega de dois lugares —
        // da lista (evento cru, tem tipo/serieMestreId) ou do form (já traz `serie`
        // e o `id` do mestre). Cobrimos as duas formas.
        const ehSerie = !!ev.serie || (ev.tipo === 'occurrence' && !!ev.serieMestreId) || ev.tipo === 'seriesMaster';
        const alvoId = (ev.tipo === 'occurrence' && ev.serieMestreId) ? ev.serieMestreId : ev.id;
        const pergunta = ehSerie
            ? `Excluir "${ev.assunto}" e TODA a série recorrente?`
            : `Excluir "${ev.assunto}" da sua agenda?`;
        if (!window.confirm(pergunta)) return false;
        try {
            const r = await fetch(`/api/ms/agenda/${encodeURIComponent(alvoId)}`, { method: 'DELETE', headers: authHeaders() });
            if (!r.ok) { setErroMsg('Não foi possível excluir.'); return false; }
            carregarEventos();
            return true;
        } catch {
            setErroMsg('Falha de rede ao excluir.');
            return false;
        }
    }, [carregarEventos]);

    return {
        estado, email, podeCriar, eventos, carregandoEventos, conectando, erroMsg, setErroMsg,
        carregarStatus, carregarEventos, conectar, desconectar, salvar, excluir,
    };
}
